// §7.7 / §9.5 / §19.4 — the shell-drawn file picker.
//
// **The gesture is the grant.** This browses with the *shell's* reach, not the app's, and hands
// back only what the user actually chose. That is only meaningful because it is drawn in the
// shell's own portal, above every `.win-host` — an app cannot draw here and cannot fake it.
//
// Not a skin component: §6.1's list is closed and has no picker. It is styled from §6.2 tokens,
// so it still looks like whichever skin is mounted.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ROOT_APPS,
  basename,
  extname,
  joinVfs,
  parentOf,
  userRoot,
  type FileEntry,
  type VfsPath,
} from '@hangar/shared';
import { useKernel, useStoreValue } from '@/kernel/react';
import type { PickerEntry } from '@/kernel/shellui';

function crumbsFor(path: VfsPath): Array<{ label: string; path: VfsPath }> {
  const crumbs: Array<{ label: string; path: VfsPath }> = [{ label: 'Hangar', path: '/' }];
  let walked = '';
  for (const segment of path.split('/').filter((part) => part.length > 0)) {
    walked = `${walked}/${segment}`;
    crumbs.push({ label: segment, path: walked });
  }
  return crumbs;
}

function PickerBody({ entry }: { entry: PickerEntry }) {
  const kernel = useKernel();
  const { request } = entry;
  const [dir, setDir] = useState<VfsPath>(request.startIn);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [selected, setSelected] = useState<readonly VfsPath[]>([]);
  const [name, setName] = useState(request.suggestedName ?? '');
  const [error, setError] = useState<string | null>(null);

  const accepts = useCallback(
    (file: FileEntry): boolean => {
      if (file.isDir) return true;
      if (request.directory) return false;
      if (request.extensions.length === 0) return true;
      return request.extensions.includes(extname(file.path));
    },
    [request.directory, request.extensions],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void kernel.vfs
      .list(dir)
      .then((listed) => {
        if (cancelled) return;
        // Directories first, then names — the order every file picker has always used.
        setEntries(
          [...listed].sort((a, b) =>
            a.isDir === b.isDir ? a.name.localeCompare(b.name) : a.isDir ? -1 : 1,
          ),
        );
        setSelected([]);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setEntries([]);
        setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [kernel, dir]);

  const visible = useMemo(() => entries.filter(accepts), [entries, accepts]);
  const crumbs = useMemo(() => crumbsFor(dir), [dir]);

  const shortcuts: Array<{ label: string; path: VfsPath }> = useMemo(() => {
    const home = userRoot(kernel.vfs.currentUser());
    return [
      { label: 'Desktop', path: joinVfs(home, 'Desktop') },
      { label: 'Documents', path: joinVfs(home, 'Documents') },
      { label: 'Downloads', path: joinVfs(home, 'Downloads') },
      { label: 'Pictures', path: joinVfs(home, 'Pictures') },
      { label: 'Apps', path: ROOT_APPS },
    ];
  }, [kernel]);

  const toggle = (file: FileEntry): void => {
    if (file.isDir && !request.directory) {
      setDir(file.path);
      return;
    }
    setSelected((current) => {
      if (!request.multiple) return [file.path];
      return current.includes(file.path)
        ? current.filter((path) => path !== file.path)
        : [...current, file.path];
    });
    if (!file.isDir) setName(file.name);
  };

  const chosen = (): VfsPath[] => {
    if (request.mode === 'save') {
      const trimmed = name.trim();
      return trimmed.length === 0 ? [] : [joinVfs(dir, trimmed)];
    }
    if (request.directory && selected.length === 0) return [dir];
    return [...selected];
  };

  const canAccept = chosen().length > 0;

  return (
    <div
      className="file-picker"
      role="dialog"
      aria-modal="true"
      aria-label={request.title}
      data-testid="file-picker"
    >
      <header className="file-picker-head">
        <h2 data-testid="file-picker-title">{request.title}</h2>
        {entry.appId ? (
          <p className="file-picker-app" data-testid="file-picker-app">
            for {entry.appId}
          </p>
        ) : null}
      </header>

      <nav className="file-picker-crumbs" aria-label="Location" data-testid="file-picker-crumbs">
        {crumbs.map((crumb) => (
          <button
            key={crumb.path}
            type="button"
            data-testid={`file-picker-crumb-${crumb.label}`}
            data-active={String(crumb.path === dir)}
            onClick={() => setDir(crumb.path)}
          >
            {crumb.label}
          </button>
        ))}
      </nav>

      <div className="file-picker-body">
        <aside className="file-picker-places" aria-label="Places">
          {shortcuts.map((place) => (
            <button
              key={place.path}
              type="button"
              data-testid={`file-picker-place-${place.label}`}
              onClick={() => setDir(place.path)}
            >
              {place.label}
            </button>
          ))}
        </aside>

        <ul className="file-picker-list" data-testid="file-picker-list">
          {dir !== '/' ? (
            <li>
              <button
                type="button"
                className="file-picker-entry"
                data-testid="file-picker-up"
                onClick={() => setDir(parentOf(dir))}
              >
                ‹ {basename(parentOf(dir))}
              </button>
            </li>
          ) : null}
          {visible.map((file) => (
            <li key={file.path}>
              <button
                type="button"
                className="file-picker-entry"
                data-kind={file.isDir ? 'dir' : 'file'}
                data-selected={String(selected.includes(file.path))}
                data-testid={`file-picker-entry-${file.name}`}
                onClick={() => toggle(file)}
                onDoubleClick={() => {
                  if (file.isDir) setDir(file.path);
                }}
              >
                {file.name}
              </button>
            </li>
          ))}
          {visible.length === 0 && !error ? (
            <li className="file-picker-empty" data-testid="file-picker-empty">
              Nothing here
            </li>
          ) : null}
          {error ? (
            <li className="file-picker-empty" data-testid="file-picker-error">
              {error}
            </li>
          ) : null}
        </ul>
      </div>

      {request.mode === 'save' ? (
        <div className="file-picker-name">
          <label htmlFor="file-picker-name-input">Name</label>
          <input
            id="file-picker-name-input"
            data-testid="file-picker-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
      ) : null}

      <footer className="file-picker-actions">
        <button
          type="button"
          data-testid="file-picker-cancel"
          onClick={() => kernel.shellui.cancelPicker(request.id)}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="file-picker-accept"
          disabled={!canAccept}
          onClick={() => kernel.shellui.acceptPicker(request.id, chosen())}
        >
          {request.mode === 'save' ? 'Save' : 'Open'}
        </button>
      </footer>
    </div>
  );
}

export function FilePicker() {
  const kernel = useKernel();
  const pickers = useStoreValue(kernel.shellui.store, (state) => state.pickers);
  const entry = pickers[0];
  if (!entry) return null;
  // Keyed by request id so switching from one pending picker to the next starts clean.
  return <PickerBody key={entry.request.id} entry={entry} />;
}
